import { Box, Button, Fade, Paper } from '@material-ui/core';
import { FormProvider, useForm } from 'react-hook-form';

import { useOnboardingStyles } from '@vyce/core/src/components/AppOnboarding';
import { FormDoc, UserFormAnswer, User } from '@vyce/core/src/types';
import { ButtonTitleWithLoading } from '@vyce/core/src/components';

import { FormAnswer } from './FormAnswer';
import { FormSignature } from './FormSignature';
import { FormSummaryTitle } from './FormSummaryTitle';
import { buildFormSummaryTitleProps } from '../utils/formSummaryTitleProps';

interface Props {
  form: FormDoc;
  loading?: boolean;
  onSave?: (data: any) => void;
  userAnswer?: UserFormAnswer;
  /** Skip outer mainContainer/paper wrappers (avoids height:100% stretching in FormResultByUser print). */
  embedded?: boolean;
  user: User;
}
export const FormToAnswer: React.FC<Props> = ({ form, loading, onSave, userAnswer, embedded, user }) => {
  const classes = useOnboardingStyles();
  const signatureBase64 = userAnswer?.signatureBase64;

  const convertTimeToDate = (value: string) => {
    const [hours, minutes] = value.split(':');
    if (hours === undefined || minutes === undefined) {
      return '';
    }
    const date = new Date();
    date.setHours(Number(hours) || 0, Number(minutes) || 0, 0, 0);
    return date;
  };

  const getDefaultValues = (userAnswer?: UserFormAnswer) => {
    const defaults: any = {};
    form?.questions?.forEach(question => {
      if (question.answer.type === 'multiple_choice') {
        defaults[question.id] = [];
      } else if (question.answer.type === 'photo') {
        defaults[question.id] = { comment: '', media: [] };
      } else {
        defaults[question.id] = '';
      }
    });
    if (userAnswer) {
      userAnswer.answers.forEach(answer => {
        if (answer.type === 'time') {
          defaults[answer.questionId] =
            typeof answer.value === 'string' && answer.value ? convertTimeToDate(answer.value) : '';
        } else if (answer.type === 'date') {
          defaults[answer.questionId] = answer.value ? new Date(answer.value as string) : '';
        } else {
          defaults[answer.questionId] = answer.value;
        }
      });
    }
    return defaults;
  };

  const methods = useForm<any>({
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: getDefaultValues(userAnswer),
  });

  const handleSubmit = () => {
    if (!onSave) {
      return;
    }
    methods.handleSubmit(onSave)();
  };

  const inner = (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        width: '100%',
      }}
    >
      <Fade in={true} timeout={200}>
        <FormSummaryTitle {...buildFormSummaryTitleProps(form, user)} />
      </Fade>

      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(handleSubmit)}
          style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
        >
          {form.questions.map(question => (
            <Fade key={question.id} in={true} timeout={200}>
              <Paper variant="outlined">
                <FormAnswer
                  question={question}
                  disabled={!!userAnswer}
                  signatureBase64={
                    question.signature
                      ? userAnswer?.answers.find(answer => answer.questionId === question.id)
                          ?.signatureBase64
                      : undefined
                  }
                />
              </Paper>
            </Fade>
          ))}
        </form>
      </FormProvider>

      {!!onSave && (
        <Paper variant="outlined" style={{ padding: 24, width: '100%' }}>
          <Box display="flex" gridGap={16} justifyContent="flex-end">
            <Button
              variant="outlined"
              style={{ width: 160 }}
              size="large"
              type="button"
              disabled={loading}
              color="primary"
              onClick={handleSubmit}
            >
              <ButtonTitleWithLoading title="Send form" loaderVariant="primary" loading={loading || false} />
            </Button>
          </Box>
        </Paper>
      )}

      {signatureBase64 && (
        <Paper variant="outlined" style={{ padding: 24, width: '100%' }}>
          <FormSignature signatureBase64={signatureBase64} />
        </Paper>
      )}
    </div>
  );

  if (embedded) {
    return inner;
  }

  return (
    <Box className={classes.mainContainer} width="100%">
      <div className={classes.paper}>{inner}</div>
    </Box>
  );
};
